import { useCallback, useState } from 'react';
import { message } from 'antd';
import { RcFile } from 'antd/lib/upload';
// import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';

import {
  checkImageAttributes,
  createSpeakerHeadshotPath,
  createThumbnailPath,
  DEFAULT_CARD_SHADE,
  DEFAUTL_CARD_COLOR,
  encodeSocialUrls,
  getSignedUrl,
  RECORDINGS_BUCKET,
  UPLOAD_BUCKET,
  uploadPostFile,
} from '../components/utils';
import { colors } from '../components/Thumbnail/Shades';
import { Giide, SocialUrls } from '../graphql/types';
import { ImageValidatorResponseType, SettingsData, UserInvite } from '../types';
import {
  DeleteAllInvitesInput,
  DeleteAllInvitesMutation,
  ImageService,
  InviteUserInput,
  InviteUserMutation,
  PublishState,
  UpdateGiideInput,
  UpdateGiideMutation,
} from '../graphql/api';
import { deleteAllInvites, inviteUser } from '../graphql/mutations';
import useMutation from './useMutation';
import { useDispatch } from 'react-redux';
import { setImageMimeTypeLoaded } from '../actions';
import { createImageUploadRecord } from './useFeedCardForm';

const updateGiideMutation = gql`
  mutation UpdateGiide($input: UpdateGiideInput!) {
    updateGiide(input: $input) {
      id
      title
      description
      unauthenticatedAccessAllowed
      cardDetails {
        cardColor
        cardShade
        thumbnailImg
        thumbnailCustomName
        thumbnailCustomPath
        thumbnailImageService
        useCustomThumbnail
      }
      speaker {
        firstName
        lastName
        bio
        title
        headshotPath
        socialUrls {
          twitter
          linkedin
          website
        }
      }
      useGiideTones
    }
  }
`;

/*const usersPermissions = gql`
  query ListUserGiidePermissionss(
    $filter: ModelUserGiidePermissionsFilterInput
  ) {
    listUserGiidePermissionss(filter: $filter) {
      items {
        email
        giideID
        userID
        createdAt
        updatedAt
      }
    }
  }
`;*/

const useGiideSettings = (giide: Giide, tenantId: string, userId: string) => {
  const [giideData, setGiideData] = useState<SettingsData>({
    id: giide.id,
    title: giide.title,
    description: giide.description || '',
    unauthenticatedAccessAllowed: giide.unauthenticatedAccessAllowed ?? true,
    publishState: giide.publishState || PublishState.unpublished,
    cardDetails: {
      cardColor:
        giide.cardDetails?.cardColor && colors[giide.cardDetails.cardColor]
          ? giide.cardDetails.cardColor
          : DEFAUTL_CARD_COLOR,
      cardShade: giide.cardDetails?.cardShade || DEFAULT_CARD_SHADE,
      thumbnailImg: giide.cardDetails?.thumbnailImg || '',
      thumbnailCustomPath: giide.cardDetails?.thumbnailCustomPath || '',
      thumbnailCustomName: giide.cardDetails?.thumbnailCustomName || '',
      thumbnailImageService:
        giide.cardDetails?.thumbnailImageService || undefined,
      useCustomThumbnail: !!giide.cardDetails?.useCustomThumbnail,
    },
    speaker: {
      firstName: giide.speaker?.firstName || '',
      lastName: giide.speaker?.lastName || '',
      fullName: giide.speaker?.fullName || '',
      bio: giide.speaker?.bio || '',
      title: giide.speaker?.title || '',
      headshotPath: giide.speaker?.headshotPath || '',
      socialUrls: {
        twitter: giide.speaker?.socialUrls?.twitter || '',
        linkedin: giide.speaker?.socialUrls?.linkedin || '',
        website: giide.speaker?.socialUrls?.website || '',
      },
    },
    useGiideTones: giide.useGiideTones || false,
    noindex: giide.noindex || false,
  });
  const [uploadAvatarProgress, setUploadAvatarProgress] = useState<number>(0);
  const [uploadThumbnailProgress, setUploadThumbnailProgress] =
    useState<number>(0);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [invites, setInvites] = useState<UserInvite[] | null>(null);
  const dispatch = useDispatch();
  const { callMutation } = useMutation<UpdateGiideInput, UpdateGiideMutation>(
    updateGiideMutation
  );

  const sendInvitesMutation = useMutation<InviteUserInput, InviteUserMutation>(
    inviteUser
  );
  const deleteAllInvitesMutation = useMutation<
    DeleteAllInvitesInput,
    DeleteAllInvitesMutation
  >(deleteAllInvites);

  const updateInput = useCallback(
    (key: string, value: string | boolean) => {
      setGiideData({
        ...giideData,
        [key]: value,
      });
    },
    [giideData]
  );

  const updateColor = useCallback(
    (cardColor: string) => {
      setGiideData({
        ...giideData,
        cardDetails: {
          ...giideData.cardDetails,
          cardColor,
          thumbnailImg: `${cardColor}_${giideData.cardDetails.cardShade}_${
            giideData.cardDetails.thumbnailImg?.slice(-2) || ''
          }`,
        },
      });
    },
    [giideData]
  );

  const updateShade = useCallback(
    (cardShade: string) => {
      setGiideData({
        ...giideData,
        cardDetails: {
          ...giideData.cardDetails,
          cardShade: cardShade,
          thumbnailImg: `${giideData.cardDetails.cardColor}_${cardShade}_${
            giideData.cardDetails.thumbnailImg?.slice(-2) || ''
          }`,
        },
      });
    },
    [giideData]
  );

  const updateThumbnail = useCallback(
    (thumbnail: string) => {
      setGiideData({
        ...giideData,
        cardDetails: {
          ...giideData.cardDetails,
          useCustomThumbnail: false,
          thumbnailImg: thumbnail,
        },
      });
    },
    [giideData]
  );

  const selectCustomThumbnail = useCallback(() => {
    setGiideData({
      ...giideData,
      cardDetails: {
        ...giideData.cardDetails,
        useCustomThumbnail: true,
      },
    });
  }, [giideData]);

  const updateSpeakerInput = useCallback(
    (key: string, value: string) => {
      setGiideData({
        ...giideData,
        speaker: {
          ...giideData.speaker,
          [key]: value,
        },
      });
    },
    [giideData]
  );
  const updateSpeakerUrlInput = useCallback(
    (key: string, value: string) => {
      setGiideData({
        ...giideData,
        speaker: {
          ...giideData.speaker,
          socialUrls: {
            ...giideData.speaker.socialUrls,
            [key]: value,
          },
        },
      });
    },
    [giideData]
  );

  const updateSpeakerName = useCallback(
    (fullName: string) => {
      setGiideData({
        ...giideData,
        speaker: {
          ...giideData.speaker,
          fullName: fullName,
        },
      });
    },
    [giideData]
  );

  const updateGiideTitleName = useCallback(
    (newTitle: string) => {
      setGiideData({
        ...giideData,
        title: newTitle,
      });
    },
    [giideData]
  );

  const removeAvatar = useCallback(() => {
    setGiideData({
      ...giideData,
      speaker: {
        ...giideData.speaker,
        headshotPath: '',
      },
    });
  }, [giideData]);

  const uploadAvatar = useCallback(
    async (file: RcFile) => {
      if (file) {
        const imageValidation: ImageValidatorResponseType =
          await checkImageAttributes(file);
        if (!imageValidation.isValid) {
          message.error(imageValidation.error);
          return false;
        }

        dispatch(setImageMimeTypeLoaded(file.type.toLowerCase()));
        setUploadAvatarProgress(1);
        const key = createSpeakerHeadshotPath(
          tenantId,
          giideData.id,
          file.name
        );
        const data = await getSignedUrl(
          `public/${key}`,
          RECORDINGS_BUCKET,
          'asset',
          'post'
        );
        const { url, fields } = data;
        if (fields && url) {
          await uploadPostFile(
            url,
            file,
            fields,
            setUploadAvatarProgress
          ).catch(() => {
            message.error('Failed to upload the image');
            return false;
          });
          setGiideData({
            ...giideData,
            speaker: {
              ...giideData.speaker,
              headshotPath: key,
            },
          });
          setUploadAvatarProgress(0);
          return true;
        }
      }
      return false;
    },
    [giideData, tenantId, dispatch]
  );

  const handleAvatarUpload = async (file: RcFile) => {
    if (file) {
      const uploaded = await uploadAvatar(file);
      if (!uploaded) {
        return Promise.reject();
      }
      setAvatar(URL.createObjectURL(file));
    }
    return Promise.reject();
  };

  const removeAvatarHandler = useCallback(() => {
    setAvatar('');
    removeAvatar();
  }, [removeAvatar]);

  const removeThumbnail = useCallback(() => {
    setGiideData({
      ...giideData,
      cardDetails: {
        ...giideData.cardDetails,
        thumbnailCustomName: '',
        thumbnailCustomPath: '',
        useCustomThumbnail: false,
      },
    });
  }, [giideData]);

  const updateThumbnailData = useCallback(
    file => {
      if (file) {
        const fileName = file.name ?? giideData.cardDetails.thumbnailCustomName;

        dispatch(setImageMimeTypeLoaded(file.type.toLowerCase()));

        const { customImagePath } = createThumbnailPath(
          tenantId,
          userId,
          giideData.id
        );

        setGiideData({
          ...giideData,
          cardDetails: {
            ...giideData.cardDetails,
            thumbnailCustomName: fileName,
            thumbnailCustomPath: customImagePath,
            thumbnailImageService: ImageService.cloudinary,
            useCustomThumbnail: true,
          },
        });
      }
    },
    [dispatch, giideData, tenantId, userId]
  );

  const uploadThumbnail = useCallback(
    async (file, returnDataAndUpload?: boolean) => {
      if (file) {
        const fileName = file.name ?? giideData.cardDetails.thumbnailCustomName;

        dispatch(setImageMimeTypeLoaded(file.type.toLowerCase()));

        setUploadThumbnailProgress(1);

        const thumbnailPathParts =
          giideData.cardDetails.thumbnailCustomPath?.split('/') || [];

        const thumbnailId = thumbnailPathParts[thumbnailPathParts.length - 1];

        const { s3Url, customImagePath } = createThumbnailPath(
          tenantId,
          userId,
          giideData.id,
          thumbnailId
        );

        // const thumbnailCustomPath = `${tenantId}/${giideData.id}/`;
        if (returnDataAndUpload) {
          const data = await getSignedUrl(
            s3Url,
            UPLOAD_BUCKET,
            'asset',
            'post'
          );

          const { url, fields } = data;

          if (fields && url) {
            await uploadPostFile(
              url,
              file,
              fields,
              setUploadThumbnailProgress
            ).catch(() => {
              message.error('Failed to upload the image');
              return false;
            });

            const imageRecordParams = {
              id: thumbnailId,
              giideId: giide.id,
              type: 'thumbnail',
              userID: userId,
              fileName: thumbnailId,
              imageUrl: 'studio/' + customImagePath,
              createdAt: String(Date.now()),
              state: 'uploading',
            };

            setGiideData({
              ...giideData,
              cardDetails: {
                ...giideData.cardDetails,
                thumbnailCustomName: fileName,
                thumbnailCustomPath: customImagePath,
                thumbnailImageService: ImageService.cloudinary,
                useCustomThumbnail: true,
              },
            });

            const encodedSocialUrls = encodeSocialUrls(
              giideData.speaker.socialUrls as SocialUrls
            );

            const giideDataInput = {
              ...giideData,
              cardDetails: {
                ...giideData.cardDetails,
                thumbnailCustomName: fileName,
                thumbnailCustomPath: customImagePath,
                thumbnailImageService: ImageService.cloudinary,
                useCustomThumbnail: true,
              },
              speaker: {
                ...giideData.speaker,
                socialUrls: encodedSocialUrls,
              },
            };

            const uploadMutations = await Promise.all([
              callMutation(giideDataInput),
              createImageUploadRecord(imageRecordParams),
            ]);

            const giideUpdated = uploadMutations[0];

            setUploadThumbnailProgress(0);

            if (giideUpdated) {
              return {
                ...giideData.cardDetails,
                thumbnailCustomName: fileName,
                thumbnailCustomPath: customImagePath,
                thumbnailImageService: 'cloudinary',
                useCustomThumbnail: true,
              };
            } else {
              return false;
            }
          }
        } else {
          setGiideData({
            ...giideData,
            cardDetails: {
              ...giideData.cardDetails,
              thumbnailCustomName: fileName,
              thumbnailCustomPath: customImagePath,
              thumbnailImageService: ImageService.cloudinary,
              useCustomThumbnail: true,
            },
          });

          setUploadThumbnailProgress(0);

          return true;
        }
      }
      setUploadThumbnailProgress(0);
      return false;
    },
    [giideData, tenantId, callMutation, dispatch, userId, giide.id]
  );

  const addInvite = useCallback(
    (email: string) => {
      const existingInvite = invites?.find(f => f.email === email);
      if (existingInvite && !existingInvite.isDeleted) {
        message.info('Email is already in list.');
        return;
      }

      if (existingInvite && invites?.length) {
        const updatedInvites = invites.map(item => {
          const temp = { ...item };
          if (item.email === email) {
            temp.isDeleted = false;
          }
          return temp;
        });
        setInvites(updatedInvites);
        return;
      }

      const invite: UserInvite = {
        email,
        giideId: giide.id,
        isNew: true,
        isDeleted: false,
      };
      const updatedInvites = invites || [];
      updatedInvites.unshift(invite);
      setInvites(updatedInvites);
    },
    [giide.id, invites]
  );

  const removeInvite = useCallback(
    (email: string) => {
      if (invites && invites.length > 0) {
        const updatedInvites = invites.map(item => {
          const temp = { ...item };
          if (item.email === email) {
            temp.isDeleted = true;
          }
          return temp;
        });
        setInvites(updatedInvites);
      }
    },
    [invites]
  );

  const saveInvites = useCallback(async () => {
    try {
      // changing giide from public to private - delete all invites
      if (
        !giide.unauthenticatedAccessAllowed &&
        giideData.unauthenticatedAccessAllowed &&
        invites &&
        invites.length > 0
      ) {
        const result = await deleteAllInvitesMutation.callMutation({
          giideId: giide.id,
        });
        if (!result || !result.data.deleteAllInvites?.success) {
          throw new Error('An error occured while deleting permissions.');
        }
      }
      // saving email invites form private giide - insert and delete
      else if (
        !giideData.unauthenticatedAccessAllowed &&
        invites &&
        invites?.length > 0
      ) {
        const validInvites = invites?.filter(
          f => !(f.isNew && f.isDeleted) && (f.isNew || f.isDeleted)
        );
        const result = await sendInvitesMutation.callMutation({
          items: validInvites as UserInvite[],
        });
        if (!result || !result.data.inviteUser?.success) {
          throw new Error('An error occured while saving permissions.');
        }
        return true;
      }
    } catch (err) {
      return false;
    }
  }, [
    giideData.unauthenticatedAccessAllowed,
    giide.unauthenticatedAccessAllowed,
    invites,
    sendInvitesMutation,
    deleteAllInvitesMutation,
    giide.id,
  ]);

  return {
    giideData,
    updateInput,
    updateColor,
    updateShade,
    updateThumbnail,
    updateSpeakerInput,
    updateSpeakerUrlInput,
    updateSpeakerName,
    removeAvatar,
    uploadAvatar,
    uploadAvatarProgress,
    uploadThumbnailProgress,
    uploadThumbnail,
    removeThumbnail,
    selectCustomThumbnail,
    avatar,
    handleAvatarUpload,
    removeAvatarHandler,
    updateGiideMutation,
    invites,
    addInvite,
    removeInvite,
    saveInvites,
    setGiideData,
    updateGiideTitleName,
    updateThumbnailData,
  };
};

export default useGiideSettings;
